/**
 * Date utility functions for handling UTC timestamps from the database.
 * 
 * The database stores all timestamps in UTC without timezone indicator.
 * These utilities ensure proper conversion to the user's local timezone
 * by appending 'Z' (UTC marker) to raw date strings before parsing.
 */

/**
 * Parse a date string from the database, treating it as UTC.
 * If the string already has timezone info (contains 'Z', '+', or 'T...+'),
 * it's parsed as-is. Otherwise, 'Z' is appended to mark it as UTC.
 */
export const parseUTCDate = (dateStr) => {
  if (!dateStr) return null;
  const str = String(dateStr).trim();
  // If already has timezone info, parse directly
  if (str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str)) {
    return new Date(str);
  }
  // Replace space between date and time with 'T' for ISO format, then append 'Z'
  const isoStr = str.replace(' ', 'T');
  return new Date(isoStr + 'Z');
};

/**
 * Format a UTC date string as "X waktu lalu" in Indonesian.
 * Automatically converts to user's local timezone.
 */
export const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = parseUTCDate(dateStr);
  if (!date || isNaN(date.getTime())) return '';
  
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 0) return 'baru saja';
  if (diff < 60) return 'baru saja';
  if (diff < 3600) return `${Math.floor(diff / 60)} menit lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)} hari lalu`;
  
  // Fallback to formatted date for old dates
  return date.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Short version of formatTimeAgo for compact UI elements.
 */
export const formatTimeAgoShort = (dateStr) => {
  if (!dateStr) return '';
  const date = parseUTCDate(dateStr);
  if (!date || isNaN(date.getTime())) return '';
  
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 0) return 'baru saja';
  if (diff < 60) return 'baru saja';
  if (diff < 3600) return `${Math.floor(diff / 60)}m lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}j lalu`;
  return `${Math.floor(diff / 86400)}h lalu`;
};

/**
 * Format a UTC date string as a full localized date with time.
 * Automatically converts to user's local timezone.
 */
export const formatFullDate = (dateStr) => {
  if (!dateStr) return '';
  const d = parseUTCDate(dateStr);
  if (!d || isNaN(d.getTime())) return '';
  
  return d.toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
